import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection/WebViewDetails.js";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import CancelablePromise from "cancelable-promise";
import { ParsedFormat, SizePreferencePolicy, getParsedFormats } from "../FormatAndSizePreference.js";
import { VideoResource } from "../../MediaResource.js";
import { VideoPlayerControls, VideoPlayerElements } from "../../Player.js";
import { PlayerTechFactoryConfig } from "../../PlayerTechFactory.js";
import { PlayerTechFactoryWithSizePreferencePolicy } from "../VideoPlayerTechFactory.js";
import { NativeVODPlayer } from "./NativeVODPlayer.js";
import { NativeVODPlayerMediaResource } from "./NativeVODPlayerMediaResource.js";
import { VideoContainerType } from "../../../../model/video/VideoFormat.js";
import { VideoInfoRawData } from "../../../../model/video/VideoInfo.js";


/**
 * Specifies the loading strategy for native VOD (Video On Demand) players.
 *
 * - `EAGER_LOAD`: Loads the video content immediately upon initialization.
 * - `LAZY_LOAD`: Delays loading the video content until it is needed (e.g., when playback starts).
 */
export enum NativeVODLoadStrategy {

    EAGER_LOAD = "eager",
    LAZY_LOAD = "lazy"
}

/**
 * Configuration options for the NativeVODPlayerFactory.
 *
 * @extends PlayerTechFactoryConfig
 *
 * @property selectedContainer - The type of video container to use for playback.
 * @property loadStrategy - The strategy used to load VOD content in the native player.
 */
export interface NativeVODPlayerFactoryConfig extends PlayerTechFactoryConfig {
    selectedContainer: VideoContainerType,
    loadStrategy: NativeVODLoadStrategy
}

/**
 * Factory class for creating and managing instances of {@link NativeVODPlayer} with support for size preference policies.
 *
 * This class extends {@link PlayerTechFactoryWithSizePreferencePolicy} and provides methods to initialize and reinitialize
 * media resources for native video-on-demand playback. It handles player configuration, format selection based on size
 * preference, and manages the loading strategy for video resources.
 *
 * @remarks
 * - Uses {@link SizePreferencePolicy} to select the optimal video format.
 * - Supports eager and lazy loading strategies via {@link NativeVODLoadStrategy}.
 * - Integrates with {@link VideoPlayerControls} for mute state and other controls.
 * - Provides cancelable promises for asynchronous resource loading.
 *
 * @example
 * ```typescript
 * const factory = new NativeVODPlayerFactory(controls, window, config, webViewDetect);
 * factory.setSizePreferencePolicy(sizePolicy);
 * const resourcePromise = factory.initMediaResource(videoInfo, playerElements);
 * ```
 *
 * @see {@link NativeVODPlayer}
 * @see {@link SizePreferencePolicy}
 * @see {@link VideoResource}
 * @see {@link PlayerTechFactoryWithSizePreferencePolicy}
 */
export class NativeVODPlayerFactory extends PlayerTechFactoryWithSizePreferencePolicy<NativeVODPlayer> {


    protected logger: Logger;
    protected sizePreference: SizePreferencePolicy;

    constructor(
        protected playerControls: VideoPlayerControls,
        protected readonly win: Window,
        public readonly config: NativeVODPlayerFactoryConfig,
        protected readonly wvDetect?: DetectedWebView
    ) {
        super(playerControls, win, config, wvDetect);
        this.logger = new BrowserLogger("NaVOD-Factory");
        this.sizePreference = null;
    }
    setSizePreferencePolicy(size: SizePreferencePolicy): void {
        this.sizePreference = size;
    }


    private load(videoInfo: VideoInfoRawData, playerPromise: Promise<NativeVODPlayer>, video: HTMLVideoElement,
        listItem: HTMLElement, parsedFormats: ParsedFormat[]): CancelablePromise<VideoResource<NativeVODPlayer>> {

        return new CancelablePromise<VideoResource<NativeVODPlayer>>((resolve, reject, onCancel) => {
            onCancel(() => {

                this.logger.debug("NaVOD reset onCancel", videoInfo)

            })


            playerPromise.then(player => {
                const startLoad = this.win.performance.now();

                const loadPromise: Promise<void> = this.config.loadStrategy == NativeVODLoadStrategy.EAGER_LOAD ? player.load() : Promise.resolve();
                loadPromise.then(_ => {
                    video.style.display = "block";
                    this.logger.debug("loaded data for videoId", videoInfo.id);
                    this.logger.debug("Preloaded Element", videoInfo);
                    const d = new NativeVODPlayerMediaResource(
                        player,
                        video,
                        listItem,
                        videoInfo,
                        this.config.playOptions.timeCutSeconds,
                        {
                            loadEndTime: this.win.performance.now(),
                            loadStartTime: startLoad
                        },
                        parsedFormats
                    )
                    resolve(d);
                })
            })

        });
    }

    protected initMediaResource(videoInfo: VideoInfoRawData, playerVideoEl: VideoPlayerElements): CancelablePromise<VideoResource<NativeVODPlayer>> {
        const listItem = playerVideoEl.mainItem;
        const video = playerVideoEl.mediaItem;

        const parsedFormats = getParsedFormats(this.config.selectedContainer, videoInfo);
        const useFormat = this.sizePreference ? this.sizePreference.pickFromFormats(parsedFormats) : parsedFormats.slice().shift();
        let playerPromise = Promise.resolve(new NativeVODPlayer(useFormat).attachVideoElement(video));
        video.style.display = "none";
        video.muted = this.playerControls.getGeneralMute();

        return this.load(videoInfo, playerPromise, video, listItem, parsedFormats)

    }

    protected reinitMediaResource(videoInfo: VideoInfoRawData, playerVideoEl: VideoPlayerElements,
        mediaRes: VideoResource<NativeVODPlayer>): CancelablePromise<VideoResource<NativeVODPlayer>> {

        const parsedFormats = getParsedFormats(this.config.selectedContainer, videoInfo);
        const useFormat = this.sizePreference ? this.sizePreference.pickFromFormats(parsedFormats) : parsedFormats.slice().shift();

        const listItem = playerVideoEl.mainItem;
        const video = mediaRes.element;
        if (playerVideoEl.mediaItem)
            playerVideoEl.mediaItem.remove();

        const player = mediaRes.player;
        const playerPromise = player.updateFormat(useFormat).then(_ => player);
        video.style.display = "none";
        video.muted = this.playerControls.getGeneralMute();

        if (playerVideoEl.mediaWrapper) {
            playerVideoEl.mediaWrapper.append(video);
        }
        return this.load(videoInfo, playerPromise, video, listItem, parsedFormats)
    }


}
